import { Component } from './component.js';
import { PricingCalculator } from './finishing-pricing-calculator.js';


/**
 * Size Selector Component
 * Handles dynamic size/style dropdown population based on project type
 *
 * @typedef {object} Refs
 * @property {HTMLElement} dynamicSizeSection
 * @property {HTMLElement} dynamicAddonsSection
 * @property {HTMLElement} monogramOptions
 * @property {HTMLInputElement} monogramText
 * @property {HTMLSelectElement} monogramPlacement
 * @property {HTMLSelectElement} monogramSize
 * @property {HTMLSelectElement} monogramThread
 * @extends {Component<Refs>}
 */
export class SizeSelectorComponent extends Component {
  requiredRefs = ['dynamicSizeSection', 'dynamicAddonsSection'];

  /** @type {object | null} */
  #pricingConfig = null;

  /** @type {string} */
  #currentProjectType = '';
  
  /** @type {number} */
  #priceMultiplier = PricingCalculator.BASE_PRICE_MULTIPLIER;

  /** @type {object | null} */
  #pillowImages = null;

  /**
   * Set pricing configuration
   * @param {object} config
   */
  setPricingConfig(config) {
    this.#pricingConfig = config;
  }

  /**
   * Set pillow style images
   * @param {object} images
   */
  setPillowImages(images) {
    this.#pillowImages = images;
  }

  /**
   * Update displayed options based on project type
   * @param {string} projectType
   */
  updateForProjectType(projectType) {

    if (!this.#pricingConfig) {
      console.error('[SizeSelector] No pricing config available!');
      return;
    }

    if (projectType === this.#currentProjectType) {
      return;
    }

    this.#currentProjectType = projectType;
    this.#renderSizeOptions(projectType);
    this.#renderAddons(projectType);
  }

  /**
   * Get current size/style selection
   * @returns {object}
   */
  getSelection() {
    const type = this.#currentProjectType;
    const details = {};
    const addons = [];

    if (type === 'pillow') {
      const sizeSelect = document.getElementById('pillow-size');
      const styleRadio = document.querySelector('input[name="pillow_style"]:checked');

      if (sizeSelect && styleRadio) {
        details.size = sizeSelect.value;
        details.style = styleRadio.value;
      }

      const selectedAddon = document.querySelector('input[name="pillow_addon"]:checked');
      if (selectedAddon) {
        addons.push(selectedAddon.value);
      }

    } else if (type === 'ornament') {
      const typeSelect = document.getElementById('ornament-type');
      const sizeSelect = document.getElementById('ornament-size');

      if (typeSelect && sizeSelect) {
        details.ornamentType = typeSelect.value;
        details.size = sizeSelect.value;
      }

      const selectedAddon = document.querySelector('input[name="ornament_addon"]:checked');
      if (selectedAddon) {
        addons.push(selectedAddon.value);
      }

    } else if (type === 'stocking') {
      const typeSelect = document.getElementById('stocking-type');
      const styleSelect = document.getElementById('stocking-style');

      if (typeSelect && styleSelect) {
        details.stockingType = typeSelect.value;
        details.style = styleSelect.value;
      }

      const selectedAddon = document.querySelector('input[name="stocking_addon"]:checked');
      if (selectedAddon) {
        addons.push(selectedAddon.value);
      }

    } else if (type === 'other') {
      const selectedRadio = document.querySelector('input[name="other_item_type"]:checked');

      if (selectedRadio) {
        const itemKey = selectedRadio.value;
        const itemData = this.#pricingConfig.other_items[itemKey];

        details.itemType = itemKey;
        details.itemLabel = itemData?.label || '';
        details.basePrice = itemData?.price || 0;
      }
    }

    return { type, details, addons };
  }

  /**
   * Helper method to populate dropdown and auto-select first option
   * @param {HTMLSelectElement} selectElement - The select element to populate
   * @param {Array} options - Array of {value, label} objects
   * @param {boolean} triggerChange - Whether to trigger pricing update
   */
  #populateAndSelectFirst(selectElement, options, triggerChange = true) {
    if (!selectElement || !options || options.length === 0) return;
    
    selectElement.innerHTML = options
      .map(opt => `<option value="${opt.value}">${opt.label}</option>`)
      .join('');
    
    // Auto-select first option
    selectElement.value = options[0].value;
    
    // Trigger pricing update if requested
    if (triggerChange) {
      const parent = this.closest('finishing-service-form-component');
      if (parent && typeof parent.onSizeChange === 'function') {
        parent.onSizeChange();
      }
    }
  }

  /**
   * Setup change event listeners for elements
   * @param {Element} container - Container element to query within
   * @param {string} selector - CSS selector for elements
   * @param {string} callbackName - Name of the parent method to call
   */
  #setupChangeListeners(container, selector, callbackName) {
    const elements = container.querySelectorAll(selector);
    elements.forEach(element => {
      element.addEventListener('change', () => {
        const parentContainer = this.closest('finishing-service-form-component');
        if (parentContainer && typeof parentContainer[callbackName] === 'function') {
          parentContainer[callbackName]();
        }
      });
    });
  }

  /**
   * Render size options for selected project type
   * @param {string} type
   */
  #renderSizeOptions(type) {
    const container = this.refs.dynamicSizeSection;

    if (!container) {
      console.error('[SizeSelector] No container found!');
      return;
    }

    let html = '';

    switch (type) {
      case 'pillow':
        html = this.#renderPillowOptions();
        break;
      case 'ornament':
        html = this.#renderOrnamentOptions();
        break;
      case 'stocking':
        html = this.#renderStockingOptions();
        break;
      case 'other':
        html = this.#renderOtherOptions();
        break;
      default:
        html = '<p class="helper-text">Please select a project type</p>';
    }

    container.innerHTML = html;

    // Setup change listeners for dynamically added selects and radios
    this.#setupChangeListeners(container, 'select', 'onSizeChange');
    this.#setupChangeListeners(container, 'input[type="radio"]', 'onSizeChange');

    // Setup ornament type change listener
    if (type === 'ornament') {
      const ornamentTypeSelect = document.getElementById('ornament-type');
      if (ornamentTypeSelect) {
        ornamentTypeSelect.addEventListener('change', () => {
          this.#updateOrnamentSizes();
          this.dispatchEvent(new CustomEvent('size-changed', { bubbles: true }));
        });
        this.#updateOrnamentSizes();
      }
    }

    // Setup pillow style change listener
    if (type === 'pillow') {
      const pillowStyleRadios = container.querySelectorAll('input[name="pillow_style"]');
      pillowStyleRadios.forEach(radio => {
        radio.addEventListener('change', () => {
          this.#updatePillowSizes();
        });
      });
      this.#updatePillowSizes();
    }

    // Setup stocking type change listener
    if (type === 'stocking') {
      const stockingTypeSelect = document.getElementById('stocking-type');
      if (stockingTypeSelect) {
        stockingTypeSelect.addEventListener('change', () => {
          this.#updateStockingStyles();
          this.#renderAddons('stocking'); // Update addons when type changes
          const parent = this.closest('finishing-service-form-component');
          if (parent && typeof parent.onSizeChange === 'function') parent.onSizeChange();
        });
        this.#updateStockingStyles();
      }
    }
  }

  /**
   * Render add-ons for selected project type
   * @param {string} type
   */
  #renderAddons(type) {
    const container = this.refs.dynamicAddonsSection;
    if (!container) return;

    // Resolve the extras object for this project type from pricing config
    const configMap = {
      pillow: this.#pricingConfig?.pillows?.extras,
      ornament: this.#pricingConfig?.ornaments?.extras,
      stocking: this.#pricingConfig?.stockings?.extras,
    };
    const extras = configMap[type];
    const hasExtras = extras && Object.keys(extras).length > 0;

    // If no extras defined, clear the section and bail out
    if (!hasExtras) {
      container.innerHTML = '';
      return;
    }

    let html = '';

    switch (type) {
      case 'pillow':
        html = this.#renderPillowAddons();
        break;
      case 'ornament':
        html = this.#renderOrnamentAddons();
        break;
      case 'stocking':
        html = this.#renderStockingAddons();
        break;
    }

    container.innerHTML = html;

    // Setup change listeners for dynamically added radio buttons
    this.#setupChangeListeners(container, 'input[type="radio"]', 'onAddonChange');
  }

  #renderPillowOptions() {
      const pillows = this.#pricingConfig.pillows;
      const styles = [
        { 
          id: 'knife_edge', 
          label: 'Knife Edge',
          description: 'A knife edge pillow has a clean, flat seam with no side band. The front needlepoint and the backing fabric are sewn together directly along the edges, creating a simple and classic pillow shape.'
        },
        { 
          id: 'box', 
          label: 'Box Pillow',
          description: 'A box pillow includes a fabric band around the sides, creating a more structured, dimensional look. The band adds depth and allows the pillow to stand up more prominently.'
        },
        { 
          id: 'box_gathered_gusset', 
          label: 'Box – Gathered Gusset',
          description: 'A gathered gusset pillow has a fabric side band that is softly gathered or ruffled instead of lying flat.'
        }
      ];

      let html = `
        <div class="form-group">
          <label>Finishing Style <span class="required">*</span></label>
          <div class="pillow-styles-grid">
      `;

      styles.forEach((style, index) => {
        const checked = index === 0 ? 'checked' : '';
        const imageUrl = this.#pillowImages?.[style.id];

        html += `
            <label class="pillow-style-card">
              <input type="radio" name="pillow_style" value="${style.id}" ${checked} required>
              <div class="style-card-content">
                <div class="style-image-placeholder">
                  ${imageUrl ? `<img src="${imageUrl}" alt="${style.label}" class="style-image">` : `<span class="style-label">${style.label}</span>`}
                </div>
                <div class="style-description">
                  <div class="style-title">${style.label}</div>
                  <div class="style-desc-text">${style.description}</div>
                </div>
              </div>
            </label>
        `;
      });

      html += `
          </div>
        </div>
        <div class="form-group">
          <label for="pillow-size">Size (Inches) <span class="required">*</span></label>
          <select id="pillow-size" name="pillow_size" class="form-control" required>
            <option value="">-- Select Size --</option>
            <!-- Populated by #updatePillowSizes -->
          </select>
        </div>
      `;

      return html;
    }

  #updatePillowSizes() {
      const styleRadio = document.querySelector('input[name="pillow_style"]:checked');
      const sizeSelect = document.getElementById('pillow-size');
      if (!styleRadio || !sizeSelect) return;

      const selectedStyleId = styleRadio.value;
      const { sizes } = this.#pricingConfig.pillows;

      const sizeOptions = sizes.map(s => {
        const rawPrice = s[selectedStyleId] || 0;
        const displayPrice = rawPrice > 0 ? ` — $${(rawPrice * this.#priceMultiplier).toFixed(2)}` : '';
        return `<option value="${s.id}">${s.label}${displayPrice}</option>`;
      }).join('');

      sizeSelect.innerHTML = `<option value="">-- Select Size --</option>${sizeOptions}`;

      // Trigger pricing update
      const parent = this.closest('finishing-service-form-component');
      if (parent && typeof parent.onSizeChange === 'function') {
        parent.onSizeChange();
      }
    }

  #renderOrnamentOptions() {
    const ornaments = this.#pricingConfig.ornaments;

    const ornamentTypeOptions = Object.keys(ornaments)
      .filter(key => key !== 'extras')
      .map(key => {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const suffix = (key === 'hanging' || key === 'boxed' || key === 'standing') ? ' Ornament' : '';
        return `<option value="${key}">${label}${suffix}</option>`;
      }).join('');

    return `
      <div class="form-group">
        <label for="ornament-type">Ornament Type <span class="required">*</span></label>
        <select id="ornament-type" name="ornament_type" class="form-control" required>
          ${ornamentTypeOptions}
        </select>
      </div>
      <div class="form-group">
        <label for="ornament-size">Size <span class="required">*</span></label>
        <select id="ornament-size" name="ornament_size" class="form-control" required>
          <!-- Populated by JS -->
        </select>
      </div>
    `;
  }

  #updateStockingStyles() {
    const typeSelect = document.getElementById('stocking-type');
    const styleSelect = document.getElementById('stocking-style');
    if (!typeSelect || !styleSelect) return;

    const stockingTypeId = typeSelect.value;
    const stockingConfig = this.#pricingConfig?.stockings;

    // Find the config using the key directly
    const selectedConfig = stockingConfig[stockingTypeId];

    if (!selectedConfig) {
      styleSelect.innerHTML = '<option value="">-- No Styles Available --</option>';
      return;
    }

    // Generate options dynamically from keys, skipping 'id' and 'label'
    const formattedOptions = Object.entries(selectedConfig)
      .filter(([key]) => key !== 'id' && key !== 'label' && typeof selectedConfig[key] === 'number')
      .map(([key, price]) => {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return {
          value: key,
          label: `${label} — $${(price * this.#priceMultiplier).toFixed(2)}`
        };
      });
    
    this.#populateAndSelectFirst(styleSelect, formattedOptions);
  }

  #updateOrnamentSizes() {
    const typeSelect = document.getElementById('ornament-type');
    const sizeSelect = document.getElementById('ornament-size');

    if (!typeSelect || !sizeSelect) return;

    const type = typeSelect.value;
    const options = this.#pricingConfig.ornaments[type];

    if (!options) return;

    const formattedOptions = options.map(opt => ({
      value: opt.size,
      label: `${opt.label} — $${(opt.price * this.#priceMultiplier).toFixed(2)}`
    }));
    
    this.#populateAndSelectFirst(sizeSelect, formattedOptions);
  }

  #renderStockingOptions() {
    const stockings = this.#pricingConfig.stockings;
    
    // Generate stocking type options dynamically from config
    const stockingTypeOptions = Object.keys(stockings)
      .filter(key => key !== 'extras')
      .map(key => {
        const item = stockings[key];
        if (item && item.label) {
          return `<option value="${key}">${item.label}</option>`;
        }
        return '';
      })
      .filter(opt => opt !== '')
      .join('');
    
    return `
      <div class="form-group">
        <label for="stocking-type">Stocking Type <span class="required">*</span></label>
        <select id="stocking-type" name="stocking_type" class="form-control" required>
          ${stockingTypeOptions}
        </select>
      </div>
      <div class="form-group">
        <label for="stocking-style">Style/Finish <span class="required">*</span></label>
        <select id="stocking-style" name="stocking_style" class="form-control" required>
          <!-- Populated by #updateStockingStyles -->
        </select>
      </div>
    `;
  }

  #renderOtherOptions() {
    const otherItems = this.#pricingConfig.other_items;

    let html = `
      <div class="other-items-info">
        <p class="helper-text">Select an item below. All prices shown are base prices and may vary based on complexity.</p>
      </div>
      <div class="other-items-grid">
    `;

    for (const [key, item] of Object.entries(otherItems)) {
      const displayPrice = (item.price * this.#priceMultiplier).toFixed(2);
      html += `
        <label class="other-item-card">
          <input type="radio" name="other_item_type" value="${key}" required>
          <div class="card-content">
            <span class="item-label">${item.label}</span>
            <span class="item-price">+$${displayPrice}</span>
          </div>
        </label>
      `;
    }

    html += `
      </div>
    `;

    return html;
  }

  /**
   * Template function for rendering addon cards
   * @param {string} addonName - Name for the radio input
   * @param {object} extras - Extras configuration object
   * @returns {string}
   */
  #renderAddonTemplate(addonName, extras) {
    let html = `
      <div class="addons-section">
        <h4>Select One Add-on (Optional)</h4>
        <div class="addons-grid">
          <label class="addon-card">
            <input type="radio" name="${addonName}" value="" checked>
            <div class="card-content">
              <span class="addon-label">No Add-on</span>
              <span class="addon-price">$0</span>
            </div>
          </label>
    `;

    for (const [key, price] of Object.entries(extras)) {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      html += `
          <label class="addon-card">
            <input type="radio" name="${addonName}" value="${key}">
            <div class="card-content">
              <span class="addon-label">${label}</span>
              <span class="addon-price">+$${price}</span>
            </div>
          </label>
      `;
    }

    html += '</div></div>';
    return html;
  }

  #renderPillowAddons() {
    return this.#renderAddonTemplate('pillow_addon', this.#pricingConfig.pillows.extras);
  }

  #renderOrnamentAddons() {
    return this.#renderAddonTemplate('ornament_addon', this.#pricingConfig.ornaments.extras);
  }

  #renderStockingAddons() {
    // Get the currently selected stocking type
    const stockingTypeSelect = document.getElementById('stocking-type');
    if (!stockingTypeSelect) {
      return '';
    }
    
    const stockingType = stockingTypeSelect.value;
    const stockingExtras = this.#pricingConfig.stockings.extras;
    
    // Get the appropriate extras based on stocking type
    let extras = {};
    if (stockingType === 'standard' && stockingExtras.standard) {
      extras = stockingExtras.standard;
    } else if (stockingType === 'mini' && stockingExtras.mini) {
      extras = stockingExtras.mini;
    }
    
    // If no extras for this type, return empty
    if (Object.keys(extras).length === 0) {
      return '';
    }
    
    return this.#renderAddonTemplate('stocking_addon', extras);
  }
}

if (!customElements.get('size-selector-component')) {
  customElements.define('size-selector-component', SizeSelectorComponent);
}
